import type { Enums, Tables } from '../../types/database.types';

/**
 * Nurture Studio (unification, Stage 2): the shapes and lenses behind the
 * six views the Apps Script app had — Monday Run, Board, Table, Calendar,
 * Campaigns, Review Queue. Lenses are pure; the database owns the rules.
 */

export type Campaign = Tables<'campaigns'>;
export type ContentItem = Tables<'content_items'>;
export type ContentActivity = Tables<'content_activity'>;
export type ContentStatus = Enums<'content_status'>;
export type CampaignStatus = Enums<'campaign_status'>;

export const CONTENT_STATUS_ORDER: ContentStatus[] = [
  'idea',
  'drafting',
  'for_review',
  'approved',
  'scheduled',
  'sent',
];

export const CONTENT_STATUS_LABELS: Record<ContentStatus, string> = {
  idea: 'Idea',
  drafting: 'Drafting',
  for_review: 'For Review',
  approved: 'Approved',
  scheduled: 'Scheduled',
  sent: 'Sent',
};

export const CONTENT_STATUS_TONES: Record<
  ContentStatus,
  'neutral' | 'brand' | 'warning' | 'success'
> = {
  idea: 'neutral',
  drafting: 'brand',
  for_review: 'warning',
  approved: 'success',
  scheduled: 'brand',
  sent: 'success',
};

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  planning: 'Planning',
  active: 'Active',
  paused: 'Paused',
  done: 'Done',
};

export const CAMPAIGN_STATUS_TONES: Record<
  CampaignStatus,
  'neutral' | 'brand' | 'warning' | 'success'
> = {
  planning: 'neutral',
  active: 'brand',
  paused: 'warning',
  done: 'success',
};

/** The live app's bucket vocabulary, offered as suggestions (free text). */
export const BUCKET_SUGGESTIONS = [
  'Thematics',
  'Sustainability Angle',
  'Tradeshow / Events',
  'Partnership Launches',
  'Product Updates',
  'Newsletter Repurpose',
];

/** Board lens: one column per status, fetch order preserved inside. */
export function contentByStatus(items: ContentItem[]): Record<ContentStatus, ContentItem[]> {
  const columns: Record<ContentStatus, ContentItem[]> = {
    idea: [],
    drafting: [],
    for_review: [],
    approved: [],
    scheduled: [],
    sent: [],
  };
  for (const item of items) {
    columns[item.status].push(item);
  }
  return columns;
}

/** Review Queue lens: what waits on the approver, oldest wait first. */
export function reviewQueue(items: ContentItem[]): ContentItem[] {
  return items
    .filter((item) => item.status === 'for_review')
    .slice()
    .sort((left, right) => left.status_changed_at.localeCompare(right.status_changed_at));
}

/** A calendar date string in the person's own timezone. */
export function localIso(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export interface CalendarDay {
  iso: string;
  dayOfMonth: number;
  inMonth: boolean;
  items: ContentItem[];
}

/**
 * Calendar lens: six Monday-first weeks around the given month, each day
 * carrying its scheduled items.
 */
export function calendarWeeks(
  year: number,
  monthIndex: number,
  items: ContentItem[],
): CalendarDay[][] {
  const byDay = new Map<string, ContentItem[]>();
  for (const item of items) {
    if (item.scheduled_on !== null) {
      byDay.set(item.scheduled_on, [...(byDay.get(item.scheduled_on) ?? []), item]);
    }
  }
  const first = new Date(year, monthIndex, 1);
  // Monday-first offset: JS Sunday=0.
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(year, monthIndex, 1 - lead);
  const weeks: CalendarDay[][] = [];
  for (let week = 0; week < 6; week += 1) {
    const days: CalendarDay[] = [];
    for (let day = 0; day < 7; day += 1) {
      const date = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate() + week * 7 + day,
      );
      const iso = localIso(date);
      days.push({
        iso,
        dayOfMonth: date.getDate(),
        inMonth: date.getMonth() === monthIndex,
        items: byDay.get(iso) ?? [],
      });
    }
    weeks.push(days);
  }
  return weeks;
}

export interface MondayRunGroup {
  iso: string;
  label: string;
  items: ContentItem[];
}

/**
 * Monday Run lens: this week's working list. Seven day-groups starting from
 * the week's Monday, plus a leading group of approved-but-unscheduled items
 * ready to be slotted in.
 */
export function mondayRun(items: ContentItem[], todayIso: string): MondayRunGroup[] {
  const today = new Date(`${todayIso}T00:00:00`);
  const lead = (today.getDay() + 6) % 7;
  const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - lead);
  const names = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const groups: MondayRunGroup[] = [];

  const ready = items.filter((item) => item.status === 'approved' && item.scheduled_on === null);
  if (ready.length > 0) {
    groups.push({ iso: '', label: 'Approved, not yet scheduled', items: ready });
  }

  for (let day = 0; day < 7; day += 1) {
    const date = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + day);
    const iso = localIso(date);
    const dayItems = items.filter((item) => item.scheduled_on === iso);
    if (dayItems.length > 0 || day < 5) {
      groups.push({ iso, label: `${names[day] ?? ''} ${iso}`, items: dayItems });
    }
  }
  return groups;
}

export function contentErrorMessage(message: string): string {
  if (message.includes('content_approval_requires_admin')) {
    return 'Only an admin can approve content or reopen approved content.';
  }
  if (message.includes('content_approval_record_is_trigger_owned')) {
    return 'The approval record is set by the system, not by hand.';
  }
  return 'That change could not be saved. Try again.';
}
